"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, readFragment } from "@opensea/graphql";
import { Media, TableCell, TableHeaderCell, TableRow, useIsLessThanMd } from "@opensea/ui-kit";
import { CollectionPreviewTooltipFragment } from "@/components/collection/CollectionPreviewTooltip/CollectionPreviewTooltip.fragment";
import { CollectionWatchlistButton, CollectionWatchlistButtonFragment } from "@/components/collection/CollectionWatchlistButton";
import { collectionRecentlyMintedFragment } from "@/components/collection/utils";
import { CollectionLink } from "@/components/items/collection/CollectionLink";
import { CollectionLinkFragment } from "@/components/items/collection/CollectionLink.fragment";
import { STATS_TABLE_COLUMN_CLASSNAMES } from "./constants";
import { StatsTableRowCollection, StatsTableRowCollectionFragment } from "./StatsTableRowCollection";
import { StatsTableRowFloorChange, StatsTableRowFloorChangeFragment } from "./StatsTableRowFloorChange";
import { StatsTableRowFloorPrice, StatsTableRowFloorPriceFragment } from "./StatsTableRowFloorPrice";
import { StatsTableRowFloorPriceMobileFragment, StatsTableRowFloorPriceMobileMobile } from "./StatsTableRowFloorPriceMobile";
import { StatsTableRowOwners, StatsTableRowOwnersFragment } from "./StatsTableRowOwners";
import { StatsTableRowSales, StatsTableRowSalesFragment } from "./StatsTableRowSales";
import { StatsTableRowSkeleton } from "./StatsTableRowSkeleton";
import { StatsTableRowSparkLineChart, StatsTableRowSparkLineChartFragment } from "./StatsTableRowSparkLineChart";
import { StatsTableRowSupply, StatsTableRowSupplyFragment } from "./StatsTableRowSupply";
import { StatsTableRowTopOffer, StatsTableRowTopOfferFragment } from "./StatsTableRowTopOffer";
import { StatsTableRowVolume, StatsTableRowVolumeFragment } from "./StatsTableRowVolume";
export const StatsTableRowFragment = graphql(`
    fragment StatsTableRow on Collection {
      id
      slug
      ...StatsTableRowFloorPrice
      ...StatsTableRowTopOffer
      ...StatsTableRowFloorChange
      ...StatsTableRowOwners
      ...StatsTableRowSales
      ...StatsTableRowSupply
      ...StatsTableRowVolume
      ...StatsTableRowCollection
      ...isRecentlyMinted
      ...CollectionLink
      ...CollectionPreviewTooltip
      ...CollectionWatchListButton
      ...StatsTableRowSparkLineChart
      ...StatsTableRowFloorPriceMobile
    }
  `, [collectionRecentlyMintedFragment, CollectionLinkFragment, StatsTableRowFloorPriceFragment, StatsTableRowTopOfferFragment, StatsTableRowFloorChangeFragment, StatsTableRowOwnersFragment, StatsTableRowSalesFragment, StatsTableRowSupplyFragment, StatsTableRowVolumeFragment, StatsTableRowCollectionFragment, CollectionPreviewTooltipFragment, CollectionWatchlistButtonFragment, StatsTableRowSparkLineChartFragment, StatsTableRowFloorPriceMobileFragment]);
export function StatsTableRow(t0) {
  const $ = _c(75);
  const {
    collection: collectionKey,
    timeframe,
    isCollectionWatchedMap,
    isCollectionWatchedFetching,
    preColumns,
    portfolio
  } = t0;
  const isLessThanMd = useIsLessThanMd();
  if (!collectionKey) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <StatsTableRowSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let T0;
  let T1;
  let collection;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[1] !== collectionKey || $[2] !== isCollectionWatchedFetching || $[3] !== isCollectionWatchedMap) {
    collection = readFragment(StatsTableRowFragment, collectionKey);
    T1 = TableHeaderCell;
    t4 = STATS_TABLE_COLUMN_CLASSNAMES.watchlist;
    T0 = CollectionWatchlistButton;
    t3 = collection;
    t1 = isCollectionWatchedFetching ?? false;
    t2 = isCollectionWatchedMap?.get(collection.slug) ?? false;
    $[1] = collectionKey;
    $[2] = isCollectionWatchedFetching;
    $[3] = isCollectionWatchedMap;
    $[4] = T0;
    $[5] = T1;
    $[6] = collection;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    T0 = $[4];
    T1 = $[5];
    collection = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2) {
    t5 = {
      fetching: t1,
      isWatched: t2
    };
    $[11] = t1;
    $[12] = t2;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== T0 || $[15] !== t3 || $[16] !== t5) {
    t6 = <T0 collection={t3} initialState={t5} />;
    $[14] = T0;
    $[15] = t3;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== T1 || $[19] !== t4 || $[20] !== t6) {
    t7 = <T1 className={t4}>{t6}</T1>;
    $[18] = T1;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const watchlist = t7;
  const t8 = isLessThanMd ? null : watchlist;
  let t9;
  if ($[22] !== collection) {
    t9 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.collection} stickyLeft={0}><StatsTableRowCollection collection={collection} /></TableCell>;
    $[22] = collection;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== collection || $[25] !== portfolio) {
    t10 = portfolio ? <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowTopOffer collection={collection} /></TableCell> : null;
    $[24] = collection;
    $[25] = portfolio;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== collection || $[28] !== isLessThanMd || $[29] !== timeframe) {
    t11 = isLessThanMd ? <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowFloorPriceMobileMobile collection={collection} timeframe={timeframe} /></TableCell> : <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowFloorPrice collection={collection} /></TableCell>;
    $[27] = collection;
    $[28] = isLessThanMd;
    $[29] = timeframe;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== collection || $[32] !== isLessThanMd || $[33] !== portfolio) {
    t12 = isLessThanMd && !portfolio ? <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.last7Days}><StatsTableRowSparkLineChart collection={collection} /></TableCell> : null;
    $[31] = collection;
    $[32] = isLessThanMd;
    $[33] = portfolio;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== collection || $[36] !== timeframe) {
    t13 = timeframe !== "ALL_TIME" ? <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowFloorChange collection={collection} timeframe={timeframe} /></TableCell> : null;
    $[35] = collection;
    $[36] = timeframe;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t13) {
    t14 = <Media greaterThanOrEqual="md">{t13}</Media>;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== collection || $[41] !== portfolio) {
    t15 = portfolio ? null : <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowTopOffer collection={collection} /></TableCell>;
    $[40] = collection;
    $[41] = portfolio;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== collection || $[44] !== timeframe) {
    t16 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowVolume collection={collection} timeframe={timeframe} /></TableCell>;
    $[43] = collection;
    $[44] = timeframe;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== collection || $[47] !== timeframe) {
    t17 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowSales collection={collection} timeframe={timeframe} /></TableCell>;
    $[46] = collection;
    $[47] = timeframe;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== collection) {
    t18 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowOwners collection={collection} /></TableCell>;
    $[49] = collection;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== collection) {
    t19 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat}><StatsTableRowSupply collection={collection} /></TableCell>;
    $[51] = collection;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== collection || $[54] !== isLessThanMd || $[55] !== portfolio) {
    t20 = !isLessThanMd || portfolio ? <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.last7Days}><StatsTableRowSparkLineChart collection={collection} /></TableCell> : null;
    $[53] = collection;
    $[54] = isLessThanMd;
    $[55] = portfolio;
    $[56] = t20;
  } else {
    t20 = $[56];
  }
  const t21 = isLessThanMd ? watchlist : null;
  let t22;
  if ($[57] !== preColumns || $[58] !== t10 || $[59] !== t11 || $[60] !== t12 || $[61] !== t14 || $[62] !== t15 || $[63] !== t16 || $[64] !== t17 || $[65] !== t18 || $[66] !== t19 || $[67] !== t20 || $[68] !== t21 || $[69] !== t8 || $[70] !== t9) {
    t22 = <TableRow interactive={true}>{t8}{t9}{preColumns}{t10}{t11}{t12}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}</TableRow>;
    $[57] = preColumns;
    $[58] = t10;
    $[59] = t11;
    $[60] = t12;
    $[61] = t14;
    $[62] = t15;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
    $[67] = t20;
    $[68] = t21;
    $[69] = t8;
    $[70] = t9;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  let t23;
  if ($[72] !== collection || $[73] !== t22) {
    t23 = <CollectionLink collection={collection}>{t22}</CollectionLink>;
    $[72] = collection;
    $[73] = t22;
    $[74] = t23;
  } else {
    t23 = $[74];
  }
  return t23;
}